import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, Share2, ShieldCheck, Truck, Minus, Plus, Check, Upload, X } from "lucide-react";
import api, { mediaUrl, formatPrice, apiError } from "@/lib/api";
import ProductPlaceholder from "@/components/ProductPlaceholder";
import ProductCard from "@/components/ProductCard";
import StarRating from "@/components/StarRating";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function ProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { add, buyNow } = useCart();
    const { has, toggle } = useWishlist();
    const { user } = useAuth();

    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [activeImg, setActiveImg] = useState(0);
    const [qty, setQty] = useState(1);
    const [zoom, setZoom] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [summary, setSummary] = useState({ total: 0, average: 0 });
    const [notFound, setNotFound] = useState(false);

    const load = () => {
        api.get(`/products/${slug}`).then(({ data }) => {
            setProduct(data.product);
            setRelated(data.related);
            setActiveImg(0);
        }).catch(() => setNotFound(true));
        api.get(`/products/${slug}/reviews`).then(({ data }) => {
            setReviews(data.reviews);
            setSummary(data.summary);
        }).catch(() => {});
    };
    useEffect(() => { load(); }, [slug]);

    if (notFound) return <div className="py-32 text-center"><p className="font-serif text-3xl">Product not found</p><Link to="/shop" className="text-gold mt-4 inline-block">Back to Shop</Link></div>;
    if (!product) return <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-14 grid md:grid-cols-2 gap-12"><div className="skeleton aspect-square" /><div className="space-y-4"><div className="skeleton h-8 w-3/4" /><div className="skeleton h-6 w-1/2" /><div className="skeleton h-24 w-full" /></div></div>;

    const images = product.images || [];
    const mainImg = images[activeImg]?.url;

    const share = async () => {
        const url = window.location.href;
        if (navigator.share) {
            try { await navigator.share({ title: product.name, url }); } catch { /* cancelled */ }
        } else {
            await navigator.clipboard.writeText(url);
            toast.success("Link copied to clipboard");
        }
    };

    const doAdd = () => { add(product, qty); toast.success("Added to cart"); };
    const doBuy = () => { buyNow(product, qty); navigate("/checkout?mode=buynow"); };
    const doWish = async () => { const a = await toggle(product.id); toast.success(a ? "Added to wishlist" : "Removed from wishlist"); };

    return (
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-8 md:py-14">
            <nav className="text-xs text-muted-foreground mb-6 flex gap-2">
                <Link to="/" className="hover:text-gold">Home</Link> / <Link to="/shop" className="hover:text-gold">Shop</Link> / <span className="text-ink">{product.name}</span>
            </nav>

            <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
                {/* Gallery */}
                <div>
                    <div
                        className={`relative overflow-hidden bg-cream-soft aspect-square ${mainImg ? "cursor-zoom-in" : ""}`}
                        onClick={() => mainImg && setZoom(true)}
                        data-testid="product-main-image"
                    >
                        {mainImg ? <img src={mediaUrl(mainImg)} alt={product.name} className="w-full h-full object-cover" /> : <ProductPlaceholder className="w-full h-full" />}
                        {product.discount_pct > 0 && <span className="absolute top-4 left-4 bg-ink text-cream text-xs px-3 py-1 tracking-wider">{product.discount_pct}% OFF</span>}
                    </div>
                    {images.length > 1 && (
                        <div className="flex gap-3 mt-4 overflow-x-auto">
                            {images.map((im, i) => (
                                <button key={im.media_id} onClick={() => setActiveImg(i)} className={`shrink-0 h-20 w-20 overflow-hidden border ${i === activeImg ? "border-gold" : "border-[#E8DFD5]"}`}>
                                    <img src={mediaUrl(im.url)} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                    {product.video_url && (
                        <video src={mediaUrl(product.video_url)} controls preload="none" playsInline className="w-full mt-4 aspect-video bg-ink object-cover" />
                    )}
                </div>

                {/* Info */}
                <div>
                    <h1 className="font-serif text-3xl lg:text-4xl tracking-tight leading-tight">{product.name}</h1>
                    {summary.total > 0 && (
                        <div className="flex items-center gap-2 mt-3">
                            <StarRating value={summary.average} />
                            <span className="text-sm text-muted-foreground">{summary.average} · {summary.total} review{summary.total !== 1 ? "s" : ""}</span>
                        </div>
                    )}
                    <div className="flex items-baseline gap-3 mt-5">
                        <span className="text-3xl font-semibold">{formatPrice(product.price)}</span>
                        {product.mrp > product.price && <span className="text-lg text-muted-foreground line-through">{formatPrice(product.mrp)}</span>}
                        {product.mrp > product.price && <span className="text-sm text-gold font-medium">Save {formatPrice(product.mrp - product.price)}</span>}
                    </div>
                    <p className="mt-5 text-muted-foreground leading-relaxed">{product.short_description}</p>

                    {product.features?.length > 0 && (
                        <ul className="mt-6 space-y-2">
                            {product.features.slice(0, 6).map((f, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm"><Check className="h-4 w-4 text-gold mt-0.5 shrink-0" strokeWidth={2} /> {f}</li>
                            ))}
                        </ul>
                    )}

                    <div className="mt-8 flex items-center gap-4">
                        <div className="flex items-center border border-ink">
                            <button data-testid="qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-cream-soft"><Minus className="h-4 w-4" /></button>
                            <span data-testid="qty-value" className="w-12 text-center">{qty}</span>
                            <button data-testid="qty-increase" onClick={() => setQty((q) => Math.min(product.stock, q + 1))} className="p-3 hover:bg-cream-soft"><Plus className="h-4 w-4" /></button>
                        </div>
                        <span className={`text-sm ${product.in_stock ? "text-muted-foreground" : "text-destructive"}`}>
                            {product.in_stock ? (product.stock <= 5 ? `Only ${product.stock} left` : "In stock") : "Out of stock"}
                        </span>
                    </div>

                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                        <button data-testid="pdp-add-to-cart" onClick={doAdd} disabled={!product.in_stock} className="flex-1 border border-ink py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-ink hover:text-cream transition-colors disabled:opacity-40">Add to Cart</button>
                        <button data-testid="pdp-buy-now" onClick={doBuy} disabled={!product.in_stock} className="flex-1 bg-gold text-white py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-gold-hover transition-colors disabled:opacity-40">Buy Now</button>
                    </div>
                    <div className="mt-4 flex items-center gap-6 text-sm text-muted-foreground">
                        <button data-testid="pdp-wishlist" onClick={doWish} className="flex items-center gap-2 hover:text-gold transition-colors"><Heart className={`h-4 w-4 ${has(product.id) ? "fill-gold text-gold" : ""}`} strokeWidth={1.5} /> Wishlist</button>
                        <button data-testid="pdp-share" onClick={share} className="flex items-center gap-2 hover:text-gold transition-colors"><Share2 className="h-4 w-4" strokeWidth={1.5} /> Share</button>
                    </div>

                    <div className="mt-8 grid grid-cols-2 gap-3 text-sm border-t border-[#E8DFD5] pt-6">
                        <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-gold" strokeWidth={1.5} /> {product.warranty || "1 Year Warranty"}</div>
                        <div className="flex items-center gap-2"><Truck className="h-5 w-5 text-gold" strokeWidth={1.5} /> Secure delivery</div>
                    </div>
                </div>
            </div>

            {/* Details tabs */}
            <div className="mt-16">
                <Tabs defaultValue="details">
                    <TabsList className="bg-transparent border-b border-[#E8DFD5] w-full justify-start rounded-none p-0 h-auto">
                        <TabsTrigger value="details" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-gold data-[state=active]:shadow-none px-5 py-3">Details</TabsTrigger>
                        <TabsTrigger value="specs" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-gold data-[state=active]:shadow-none px-5 py-3">Specifications</TabsTrigger>
                        <TabsTrigger value="reviews" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-gold data-[state=active]:shadow-none px-5 py-3">Reviews ({summary.total})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="details" className="pt-8">
                        <p className="text-muted-foreground leading-relaxed max-w-3xl whitespace-pre-line">{product.description}</p>
                        {product.whats_in_box?.length > 0 && (
                            <div className="mt-8">
                                <h4 className="font-medium mb-3">What's in the Box</h4>
                                <ul className="space-y-2 text-sm text-muted-foreground">{product.whats_in_box.map((b, i) => <li key={i} className="flex gap-2"><Check className="h-4 w-4 text-gold mt-0.5" /> {b}</li>)}</ul>
                            </div>
                        )}
                        {product.temperature_settings?.length > 0 && (
                            <div className="mt-8">
                                <h4 className="font-medium mb-3">Temperature Settings</h4>
                                <div className="flex flex-wrap gap-2">{product.temperature_settings.map((t, i) => <span key={i} className="border border-[#E8DFD5] px-3 py-1 text-sm">{t}</span>)}</div>
                            </div>
                        )}
                        {product.shipping_info && <div className="mt-8"><h4 className="font-medium mb-2">Shipping</h4><p className="text-sm text-muted-foreground">{product.shipping_info}</p></div>}
                    </TabsContent>

                    <TabsContent value="specs" className="pt-8">
                        {product.specifications?.length > 0 ? (
                            <div className="max-w-2xl divide-y divide-[#E8DFD5]">
                                {product.specifications.map((s, i) => (
                                    <div key={i} className="flex justify-between py-3 text-sm"><span className="text-muted-foreground">{s.label}</span><span className="font-medium">{s.value}</span></div>
                                ))}
                            </div>
                        ) : <p className="text-muted-foreground">No specifications available.</p>}
                    </TabsContent>

                    <TabsContent value="reviews" className="pt-8">
                        <ReviewsSection slug={slug} reviews={reviews} summary={summary} user={user} onSubmitted={load} />
                    </TabsContent>
                </Tabs>
            </div>

            {related.length > 0 && (
                <div className="mt-20">
                    <h2 className="font-serif text-2xl md:text-3xl mb-8">You May Also Like</h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">{related.map((p) => <ProductCard key={p.id} product={p} />)}</div>
                </div>
            )}

            {zoom && mainImg && (
                <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setZoom(false)}>
                    <button className="absolute top-6 right-6 text-white"><X className="h-7 w-7" /></button>
                    <img src={mediaUrl(mainImg)} alt={product.name} className="max-w-full max-h-full object-contain" />
                </div>
            )}
        </div>
    );
}

function ReviewsSection({ slug, reviews, summary, user, onSubmitted }) {
    const [rating, setRating] = useState(5);
    const [text, setText] = useState("");
    const [photos, setPhotos] = useState([]);
    const [video, setVideo] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const photoRef = useRef();
    const videoRef = useRef();

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const fd = new FormData();
        fd.append("rating", rating);
        fd.append("text", text);
        photos.forEach((p) => fd.append("photos", p));
        if (video) fd.append("video", video);
        try {
            const { data } = await api.post(`/products/${slug}/reviews`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(data.message);
            setText(""); setPhotos([]); setVideo(null); setRating(5);
            onSubmitted();
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
                {reviews.length === 0 ? (
                    <p className="text-muted-foreground">No reviews yet. Be the first to share your experience.</p>
                ) : reviews.map((r) => (
                    <div key={r.id} className="border-b border-[#E8DFD5] pb-6" data-testid={`review-${r.id}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <StarRating value={r.rating} />
                                <span className="text-sm font-medium">{r.customer_name}</span>
                                {r.verified_purchase && <span className="text-[10px] uppercase tracking-wider bg-gold/15 text-gold px-2 py-0.5">Verified Purchase</span>}
                            </div>
                            <span className="text-xs text-muted-foreground">{(r.created_at || "").slice(0, 10)}</span>
                        </div>
                        {r.text && <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{r.text}</p>}
                        {r.media?.length > 0 && (
                            <div className="flex flex-wrap gap-3 mt-4">
                                {r.media.map((m, i) => m.kind === "video" ? (
                                    <video key={i} src={mediaUrl(m.url)} controls preload="none" playsInline className="h-32 rounded bg-ink" />
                                ) : (
                                    <img key={i} src={mediaUrl(m.url)} alt="review" className="h-24 w-24 object-cover rounded" />
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="bg-cream-soft p-6 h-fit">
                <h4 className="font-serif text-xl mb-4">Write a Review</h4>
                {!user ? (
                    <p className="text-sm text-muted-foreground">Please <Link to="/login" className="text-gold underline">log in</Link> to submit a review.</p>
                ) : (
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Rating</label>
                            <div className="flex gap-1 mt-1">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <button key={i} type="button" onClick={() => setRating(i)} data-testid={`rating-star-${i}`}>
                                        <span className={`text-2xl ${i <= rating ? "text-gold" : "text-gold/30"}`}>★</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <textarea data-testid="review-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Share your experience…" rows={4} className="w-full bg-white border border-[#E8DFD5] p-3 text-sm outline-none focus:border-gold" />
                        <input ref={photoRef} type="file" accept="image/*" multiple hidden onChange={(e) => setPhotos(Array.from(e.target.files))} />
                        <input ref={videoRef} type="file" accept="video/*" hidden onChange={(e) => setVideo(e.target.files[0])} />
                        <div className="flex gap-3">
                            <button type="button" onClick={() => photoRef.current.click()} className="flex-1 text-xs border border-[#E8DFD5] bg-white py-2 flex items-center justify-center gap-1.5"><Upload className="h-3.5 w-3.5" /> Photos {photos.length ? `(${photos.length})` : ""}</button>
                            <button type="button" onClick={() => videoRef.current.click()} className="flex-1 text-xs border border-[#E8DFD5] bg-white py-2 flex items-center justify-center gap-1.5"><Upload className="h-3.5 w-3.5" /> Video {video ? "✓" : ""}</button>
                        </div>
                        <button data-testid="review-submit" disabled={submitting} className="w-full bg-ink text-cream py-3 text-sm uppercase tracking-[0.15em] hover:bg-gold transition-colors disabled:opacity-50">{submitting ? "Submitting…" : "Submit Review"}</button>
                        <p className="text-[11px] text-muted-foreground">Reviews are published after admin approval.</p>
                    </form>
                )}
            </div>
        </div>
    );
}
